<script setup>
import { OverlayBadge } from '@dashhudson/dashing-ui/badge';
import { Button } from '@dashhudson/dashing-ui/button';
import { DropdownBase } from '@dashhudson/dashing-ui/dropdown-panel';
import { Menu } from '@dashhudson/dashing-ui/menu';
import { SelectButton } from '@dashhudson/dashing-ui/select-button';
import { Skeleton } from '@dashhudson/dashing-ui/skeleton';
import { useElementSize, useScroll } from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import { computed, onBeforeMount, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

import {
  SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE,
  SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE,
} from '@/app/scheduler/constants';
import { MIXPANEL_VALUES } from '@/components/layout/navigation/constants';
import NotificationsPanelItem from '@/components/layout/navigation/NotificationsPanelItem.vue';
import { useRegisterSockets } from '@/composables/useRegisterSockets';
import { COMMENT_RESOURCE_TYPES } from '@/config';
import { useCommentingStore } from '@/stores/commenting';
import { useIdentityStore } from '@/stores/identity';
import { useSchedulerStore } from '@/stores/scheduler';
import { useTrackingStore } from '@/stores/tracking';
import { trackNavigation } from '@/utils/analytics/tracking';

const ALL = 'all';
const UNREAD = 'unread';

const router = useRouter();

const commentingStore = useCommentingStore();
const identityStore = useIdentityStore();
const schedulerStore = useSchedulerStore();

const trackingStore = useTrackingStore();

const shown = ref(false);
const scrollRef = useTemplateRef('scroll');
const { y: scrollY } = useScroll(scrollRef);
const { height } = useElementSize(scrollRef);
const maxHeight = ref(0);

const selectedFilter = ref(ALL);

const showingAll = computed(() => selectedFilter.value === ALL);
const filteringUnread = computed(() => selectedFilter.value === UNREAD);

const filterOptions = computed(() => [
  { label: 'All', value: ALL },
  { label: 'Unread', value: UNREAD },
]);

function loadFirstPage() {
  commentingStore.getNotifications();
}

function loadNextPage() {
  commentingStore.getNotificationsNextPage();
}

const notifications = computed(() => commentingStore.notifications);
const displayedNotifications = computed(() => {
  if (filteringUnread.value) {
    return commentingStore.unreadNotifications;
  }
  return notifications.value;
});
const notificationsPaging = computed(() => commentingStore.notificationsPaging);
const pending = computed(() => commentingStore.pending);

const canLoadNextPage = computed(
  () =>
    !pending.value.notificationsPaging &&
    !pending.value.notifications &&
    !!notificationsPaging.value.next &&
    showingAll.value,
);

const shouldLoadNextPage = computed(
  () => canLoadNextPage.value && notifications.value.every((notification) => !notification.readAt),
);

const notificationsCount = computed(() => notifications.value.length);

const unreadNotificationsCount = computed(() =>
  notifications.value.reduce(
    (count, notification) => (!notification.readAt ? count + 1 : count),
    0,
  ),
);

const hasUnreadNotifications = computed(() =>
  notifications.value.some((notification) => !notification.readAt),
);

onBeforeMount(() => {
  loadFirstPage();
});

watch(
  () => pending,
  (to, from) => {
    if (isEqual(to, from)) {
      return;
    }
    if (
      !to.notifications &&
      !to.notificationsPaging &&
      notificationsPaging.value.next &&
      notifications.value?.every((notification) => !notification.readAt)
    ) {
      loadNextPage();
    }
  },
);

watch(
  () => shouldLoadNextPage.value,
  (to, from) => {
    if (isEqual(to, from)) {
      return;
    }
    if (to) {
      loadNextPage();
    }
  },
);

function sendMenuOptionSelectedEvent(option) {
  trackingStore.track('Notification Panel Option Selection', {
    'Notifications Count': notificationsCount.value,
    'Unread Notifications Count': unreadNotificationsCount.value,
    Option: option,
  });
}

function sendNotificationClickedEvent(notification) {
  let action = null;
  let properties = null;

  if (notification?.comment !== undefined && notification?.comment !== null) {
    action = 'Notification Panel View Comment';
    properties = {
      'Resource ID': notification.comment.resourceId,
      'Resource Type': notification.comment.resourceType,
      'Unread Notification': notification.readAt == null,
    };
  }

  if (action !== null && properties !== null) {
    trackingStore.track(action, properties);
  }
}

const isNearBottom = computed(() => {
  if (!scrollRef.value) return false;

  const { scrollHeight, clientHeight } = scrollRef.value;
  const scrollTop = scrollY.value;
  const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

  return distanceFromBottom <= 100;
});

watch(
  () => [isNearBottom.value, canLoadNextPage.value],
  ([bottom, can]) => {
    if (bottom && can) {
      loadNextPage();
    }
  },
);

async function clickNotification(notification) {
  sendNotificationClickedEvent(notification);
  await commentingStore.markNotificationRead({ id: notification.id });
  const { url } = notification;

  let path = url.match(/^http/) ? url.replace(new URL(notification.url).origin, '') : url;

  if (notification.comment.resourceType === COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT) {
    path = path
      .replace(`&resource_id=${notification.comment.id}`, '')
      .replace('/assigned-to-me', '/all-messages');
  }
  if (notification.comment.resourceType === COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE) {
    path = path.replace(
      `id=${notification.comment.resourceId}`,
      `mentioned_user_ids=${identityStore.identity?.id}`,
    );
  }

  if (
    [
      ...Object.values(SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE),
      ...Object.values(SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE),
    ].includes(notification.comment.resourceType)
  ) {
    await schedulerStore.fetchPost({
      platform: notification.url.split('/')[5],
      id: notification.comment.resourceId,
    });
  }

  if (notification.comment.brandId === identityStore.currentBrand?.id) {
    await router.push(path);
  } else {
    window.location.assign(path);
  }
}

const subMenuContent = computed(() => {
  const dropdownMenuButtonItemList = [
    {
      label: 'Mark All as Read',
      command() {
        sendMenuOptionSelectedEvent('Mark All as Read');
        commentingStore.markNotificationsRead();
      },
    },
    {
      label: 'Email Preferences',
      command() {
        router.push({
          name: 'settings.notifications.email',
          params: { brandLabel: identityStore.currentBrandLabel },
        });
        sendMenuOptionSelectedEvent('Email Preferences');
      },
    },
  ];
  return dropdownMenuButtonItemList;
});

function createCommentNotificationSocket(payload) {
  commentingStore.createCommentNotificationSocket({ notification: payload });
}

function trackOpen(isBeingOpened) {
  if (isBeingOpened) {
    trackNavigation(MIXPANEL_VALUES.NOTIFICATIONS.ROOT);
  }
}

const displayMessage = computed(() => {
  if (showingAll.value && !notificationsPaging.value.next) {
    return 'No more notifications';
  }
  if (
    filteringUnread.value &&
    Array.isArray(commentingStore.unreadNotifications) &&
    !commentingStore.unreadNotificationsPaging.next
  ) {
    if (commentingStore.unreadNotifications.length === 0) {
      return 'No unread notifications';
    }
    return 'No more unread notifications';
  }
  return null;
});

const hasDisplayMessage = computed(() => !!displayMessage.value);

useRegisterSockets('COMMENT_NOTIFICATION_CREATED', createCommentNotificationSocket);

const scrollStyle = computed(() => ({
  minHeight: maxHeight.value > 0 ? `${maxHeight.value}px` : undefined,
}));

const showLoading = computed(() => {
  const allLoading =
    showingAll.value && (pending.value.notificationsPaging || pending.value.notifications);
  const unreadLoading = filteringUnread.value && pending.value.unreadNotifications;
  return allLoading || unreadLoading;
});

watch(height, (newHeight) => {
  if (!showLoading.value) {
    if (newHeight > maxHeight.value) {
      maxHeight.value = newHeight;
    }
  }
});

const canLoadUnreadNotifications = computed(
  () =>
    !commentingStore.pending.unreadNotifications &&
    !!commentingStore.unreadNotificationsPaging?.next,
);

watch(
  () => shown.value,
  (to) => {
    if (to) {
      selectedFilter.value = ALL;
      commentingStore.resetUnreadNotifications();
      commentingStore.getUnreadNotifications();
    }
  },
);

watch(
  () => [isNearBottom.value, canLoadUnreadNotifications.value],
  ([bottom, can]) => {
    if (bottom && can) {
      commentingStore.getUnreadNotifications();
    }
  },
);
</script>

<template>
  <DropdownBase
    v-bind="{ disabled: false, placement: 'right-end' }"
    v-model:shown="shown"
    strategy="fixed"
    popper-classes="rounded-lg overflow-hidden"
    @update:shown="trackOpen($event)"
  >
    <OverlayBadge
      :show="hasUnreadNotifications"
      size="xs"
      badge-class="translate-x-[-12px] translate-y-[9px]"
    >
      <Button severity="utility" icon="bell" size="lg" :active="shown" />
    </OverlayBadge>
    <template #content>
      <div
        ref="scroll"
        class="scrollbar flex max-h-152 min-h-20 max-w-66 min-w-66 flex-col overflow-x-hidden overflow-y-auto"
        :style="scrollStyle"
      >
        <div class="backdrop-menu sticky top-0 z-20 px-6 pt-6 pb-2">
          <div class="flex h-8 items-center justify-between">
            <h5 class="typo-base-semibold text-primary">Notifications</h5>
            <Menu :model="subMenuContent" size="xs" />
          </div>
          <div>
            <SelectButton
              v-model="selectedFilter"
              :options="filterOptions"
              :allow-empty="false"
              spacing="sm"
              variant="light"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1 px-6 pb-6">
          <NotificationsPanelItem
            v-for="notification in displayedNotifications"
            :key="notification.id"
            :notification="notification"
            :brand="identityStore.identityBrandsById[notification.comment.brandId]"
            @click="clickNotification(notification)"
          />
          <template v-if="showLoading">
            <Skeleton v-for="i in 5" :key="i" height="140px" border-radius="16px" />
          </template>
          <template v-else-if="hasDisplayMessage">
            <p class="typo-base text-primary py-6 text-center">
              {{ displayMessage }}
            </p>
          </template>
        </div>
      </div>
    </template>
  </DropdownBase>
</template>
